import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "cavilow_install_banner_dismissed_at";
const DISMISS_DAYS = 14; // re-show banner after 14 days if dismissed

// Capture beforeinstallprompt the moment it fires — even before this component mounts.
let deferredPrompt = null;
const promptListeners = new Set();
let installedListeners = new Set();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    promptListeners.forEach((cb) => cb(e));
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installedListeners.forEach((cb) => cb());
  });
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true
  );
}

function isIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return isIOS && isSafari;
}

function wasRecentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    const ageMs = Date.now() - ts;
    return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallBanner() {
  // null = checking, false = hidden, "prompt" = installable via prompt, "ios" = iOS instructions
  const [mode, setMode] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    // Skip entirely if already installed or recently dismissed
    if (isStandalone()) { setMode(false); return; }
    if (wasRecentlyDismissed()) { setMode(false); return; }

    // If beforeinstallprompt has already fired, show now
    if (deferredPrompt) { setMode("prompt"); return; }

    // iOS Safari has no beforeinstallprompt — show "Add to Home Screen" hint
    if (isIOSSafari()) { setMode("ios"); return; }

    // Otherwise wait for beforeinstallprompt
    const onPrompt = () => setMode("prompt");
    const onInstalled = () => setMode(false);
    promptListeners.add(onPrompt);
    installedListeners.add(onInstalled);
    return () => {
      promptListeners.delete(onPrompt);
      installedListeners.delete(onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setMode(false);
  };

  const install = async () => {
    if (mode === "ios") { setShowIosHelp(true); return; }
    if (!deferredPrompt) {
      toast.message("Use the browser's install icon in the address bar.");
      return;
    }
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      deferredPrompt = null;
      if (choice?.outcome === "accepted") {
        toast.success("Cavilow is installing…");
        setMode(false);
      } else {
        dismiss();
      }
    } catch {
      toast.error("Install was blocked by the browser.");
    }
  };

  if (mode !== "prompt" && mode !== "ios") return null;

  return (
    <>
      <div
        data-testid="install-banner"
        className="fixed left-1/2 -translate-x-1/2 bottom-4 sm:left-auto sm:translate-x-0 sm:right-6 sm:bottom-6 z-[70] w-[calc(100%-2rem)] sm:w-[380px]"
      >
        <div className="rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/15 p-4 sm:p-5 flex items-start gap-3 fade-up">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[var(--cavilo-blue)] text-white shrink-0">
            <Download size={22} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm sm:text-base text-[var(--cavilo-ink)]">
              Install Cavilow
            </div>
            <div className="text-xs sm:text-[13px] text-slate-500 mt-0.5 leading-snug">
              Get one-tap access from your home screen.
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="install-banner-install"
                onClick={install}
                className="rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-4 py-2 text-xs font-bold btn-press"
              >
                Install
              </button>
              <button
                data-testid="install-banner-dismiss"
                onClick={dismiss}
                className="rounded-xl text-xs font-semibold text-slate-500 hover:text-[var(--cavilo-ink)] px-3 py-2"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            data-testid="install-banner-close"
            onClick={dismiss}
            aria-label="Dismiss"
            className="grid place-items-center w-8 h-8 rounded-xl text-slate-400 hover:text-[var(--cavilo-ink)] hover:bg-slate-100 shrink-0"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* iOS help modal — appears only after user taps Install on iOS Safari */}
      {showIosHelp && (
        <div
          data-testid="install-ios-help"
          className="fixed inset-0 z-[90] grid place-items-center px-4 py-8"
          onClick={() => setShowIosHelp(false)}
        >
          <div className="absolute inset-0 bg-[var(--cavilo-ink)]/60 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-3xl shadow-2xl border border-slate-200 max-w-md w-full p-7 fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display font-bold text-xl text-[var(--cavilo-ink)]">
              Install Cavilow on iPhone
            </h3>
            <ol className="mt-4 space-y-3">
              {[
                "Tap the Share icon at the bottom of Safari.",
                "Scroll down and select 'Add to Home Screen'.",
                "Tap Add — Cavilow will appear on your home screen.",
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                  <span className="grid place-items-center w-6 h-6 shrink-0 rounded-full bg-[var(--cavilo-blue)] text-white text-[11px] font-bold">
                    {i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            <button
              data-testid="install-ios-help-close"
              onClick={() => { setShowIosHelp(false); dismiss(); }}
              className="mt-6 w-full rounded-2xl bg-[var(--cavilo-ink)] text-white py-3 text-sm font-bold hover:bg-[#1a2237] btn-press"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Programmatic trigger for any other "Install" buttons elsewhere in the app.
export async function triggerInstall() {
  if (isStandalone()) {
    toast.success("Cavilow is already installed.");
    return true;
  }
  if (deferredPrompt) {
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      deferredPrompt = null;
      return choice?.outcome === "accepted";
    } catch {
      return false;
    }
  }
  if (isIOSSafari()) {
    toast.message("In Safari: tap Share → 'Add to Home Screen' to install Cavilow.");
    return false;
  }
  toast.message("Use your browser's install icon in the address bar.");
  return false;
}
